<script setup>
// Round Robin Dashboard
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAutoRefresh } from 'dashboard/composables/useAutoRefresh';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import ReportFilterSelector from './components/FilterSelectorRoundRobin.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import MetricsCards from './components-round-robin/MetricsCards.vue';
import AgentsTable from './components-round-robin/AgentsTable.vue';
import DistributionChart from './components-round-robin/DistributionChart.vue';
import PerformanceChart from './components-round-robin/PerformanceChart.vue';
import TimelineList from './components-round-robin/TimelineList.vue';
import ReassignmentsTable from './components-round-robin/ReassignmentsTable.vue';

const { t } = useI18n();
const store = useStore();

// Store getters
const reportData = computed(
  () => store.getters['roundRobinReports/getReportData']
);
const lastUpdate = computed(
  () => store.getters['roundRobinReports/getLastUpdate']
);
const uiFlags = computed(() => store.getters['roundRobinReports/getUIFlags']);
const filters = computed(() => store.getters['roundRobinReports/getFilters']);
const selectedAgent = computed(
  () => store.getters['roundRobinReports/getSelectedAgent']
);
const displayMetrics = computed(
  () => store.getters['roundRobinReports/getDisplayMetrics']
);
const filteredOnlineAgents = computed(
  () => store.getters['roundRobinReports/getFilteredOnlineAgents']
);
const filteredOfflineAgents = computed(
  () => store.getters['roundRobinReports/getFilteredOfflineAgents']
);
const performanceData = computed(
  () => store.getters['roundRobinReports/getPerformanceData']
);
const filteredTimeline = computed(
  () => store.getters['roundRobinReports/getFilteredTimeline']
);
const filteredReassignments = computed(
  () => store.getters['roundRobinReports/getFilteredReassignments']
);

const loading = computed(() => uiFlags.value.isFetching);
const isExporting = computed(() => uiFlags.value.isExporting);
const isProcessing = computed(() => uiFlags.value.isProcessing);
const processingMessage = computed(() => uiFlags.value.processingMessage);

// 🔥 NOVO: Computed para loading durante filtros (mas já tem dados)
const isRefreshing = computed(() => loading.value && reportData.value);

// Methods
const fetchData = async () => {
  try {
    await store.dispatch('roundRobinReports/fetchReport');
  } catch (error) {
    console.error('Error fetching report data:', error);
    useAlert(t('ROUND_ROBIN.ERRORS.FETCH_FAILED'));
  }
};

// Auto-refresh composable (must be after fetchData declaration)
const autoRefresh = useAutoRefresh(fetchData, 60);

const exportData = async () => {
  try {
    const fileName = generateFileName({
      type: 'Round Robin Report',
      to: filters.value.to,
    });

    await store.dispatch('roundRobinReports/exportReport', fileName);
    useAlert(t('ROUND_ROBIN.SUCCESS.EXPORT'));
  } catch (error) {
    console.error('Error exporting data:', error);
    useAlert(t('ROUND_ROBIN.ERRORS.EXPORT_FAILED'));
  }
};

const formatDate = date => {
  if (!date) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(date));
};

const handleAgentClick = agentId => {
  const currentSelectedId = filters.value.selectedAgentId;
  if (currentSelectedId === agentId) {
    store.dispatch('roundRobinReports/clearSelectedAgent');
  } else {
    store.dispatch('roundRobinReports/setSelectedAgent', agentId);
  }
};

const handleAgentClickByName = agentName => {
  if (!reportData.value) return;

  const allAgents = [
    ...(reportData.value.online_agents || []),
    ...(reportData.value.offline_agents || []),
  ];

  const agent = allAgents.find(a => a.name === agentName);
  if (agent) {
    handleAgentClick(agent.id);
  }
};

const clearAgentFilter = () => {
  store.dispatch('roundRobinReports/clearSelectedAgent');
};

const onFilterChange = async updatedFilter => {
  // Build filters object
  const newFilters = {
    from: updatedFilter.from,
    to: updatedFilter.to,
    teamId: updatedFilter.selectedTeam?.id || null,
    inboxId: updatedFilter.selectedInbox?.id || null,
    agentIds:
      updatedFilter.selectedAgents?.length > 0
        ? updatedFilter.selectedAgents.map(a => a.id).join(',')
        : null,
  };

  // Update filters in store
  await store.dispatch('roundRobinReports/updateFilters', newFilters);

  // Fetch new data
  await fetchData();
};

// Lifecycle
onMounted(async () => {
  // Aguarda um pouco para dar tempo do time padrão ser selecionado
  await new Promise(resolve => setTimeout(resolve, 500));
  await fetchData();
  autoRefresh.start();
});

onUnmounted(() => {
  autoRefresh.stop();
  store.dispatch('roundRobinReports/clearReport');
});
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-rotate-cw"
        accent="orange"
        :title="$t('ROUND_ROBIN.HEADER')"
        :subtitle="$t('ROUND_ROBIN.SUBTITLE')"
      >
        <template #actions>
          <!-- Auto Refresh Toggle -->
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11 whitespace-nowrap">
              Auto-refresh
            </span>
            <ToggleSwitch v-model="autoRefresh.enabled.value" />
          </div>

          <!-- Export Button -->
          <button
            :disabled="loading || isExporting || isProcessing"
            class="inline-flex items-center px-4 py-2 border border-n-weak rounded-lg text-sm font-medium text-n-slate-11 bg-n-solid-2 hover:bg-n-solid-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-blue-9 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="exportData"
          >
            <svg
              class="w-4 h-4 mr-2"
              :class="{ 'animate-spin': isExporting }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                v-if="!isExporting"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
              <circle
                v-else
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
            </svg>
            {{ isExporting ? 'Exportando...' : 'Exportar CSV' }}
          </button>

          <!-- Refresh Button -->
          <button
            :disabled="loading || isProcessing"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-n-blue-9 hover:bg-n-blue-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-blue-9 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="fetchData"
          >
            <svg
              class="w-4 h-4 mr-2"
              :class="{ 'animate-spin': loading || isProcessing }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ loading || isProcessing ? 'Carregando...' : 'Atualizar' }}
          </button>
        </template>
      </PageHeader>
    </template>

    <!-- Last Update Info -->
    <div v-if="lastUpdate" class="pb-2 text-xs text-n-slate-10">
      Última atualização: {{ formatDate(lastUpdate) }}
      <span v-if="autoRefresh.enabled.value" class="ml-2">
        • Próxima atualização em {{ autoRefresh.countdown.value }}s
      </span>
    </div>

    <!-- Filters -->
    <ReportFilterSelector
      :show-business-hours-switch="false"
      :show-agents-filter="true"
      :show-team-filter="true"
      :show-inbox-filter="true"
      :default-team-selection="true"
      max-date-range-option="LAST_7_DAYS"
      @filter-change="onFilterChange"
    />

    <!-- Processing State -->
    <div v-if="isProcessing" class="flex items-center justify-center py-20">
      <div class="text-center max-w-md">
        <div class="relative">
          <svg
            class="animate-spin h-16 w-16 text-n-blue-9 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-4xl">📊</span>
          </div>
        </div>
        <p class="mt-6 text-lg font-medium text-n-slate-12">
          Gerando Relatório
        </p>
        <p class="mt-2 text-sm text-n-slate-11">{{ processingMessage }}</p>
        <p class="mt-4 text-xs text-n-slate-10">
          Este processo pode levar alguns segundos devido ao volume de dados...
        </p>
        <div class="mt-6 flex justify-center gap-2">
          <div
            class="w-2 h-2 rounded-full bg-n-blue-9 animate-bounce"
            style="animation-delay: 0ms"
          />
          <div
            class="w-2 h-2 rounded-full bg-n-blue-9 animate-bounce"
            style="animation-delay: 150ms"
          />
          <div
            class="w-2 h-2 rounded-full bg-n-blue-9 animate-bounce"
            style="animation-delay: 300ms"
          />
        </div>
      </div>
    </div>

    <!-- Loading State (Initial) -->
    <div v-else-if="loading && !reportData">
      <div class="py-6 space-y-6">
        <!-- Skeleton Metrics Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="i in 4"
            :key="i"
            class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl p-6 animate-pulse"
          >
            <div class="h-4 bg-n-slate-4 rounded w-24 mb-3" />
            <div class="h-8 bg-n-slate-4 rounded w-16" />
          </div>
        </div>

        <!-- Skeleton Charts -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div
            v-for="i in 2"
            :key="i"
            class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl p-6 animate-pulse"
          >
            <div class="h-6 bg-n-slate-4 rounded w-48 mb-6" />
            <div class="h-64 bg-n-slate-4 rounded" />
          </div>
        </div>

        <!-- Skeleton Table -->
        <div
          class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl p-6 animate-pulse"
        >
          <div class="h-6 bg-n-slate-4 rounded w-32 mb-6" />
          <div class="space-y-3">
            <div v-for="i in 5" :key="i" class="h-12 bg-n-slate-4 rounded" />
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="reportData" class="relative">
      <!-- 🔥 NOVO: Loading Overlay durante refresh de filtros -->
      <transition name="fade">
        <div
          v-if="isRefreshing"
          class="absolute inset-0 bg-n-slate-1/80 dark:bg-n-slate-1/80 backdrop-blur-sm z-50 flex items-center justify-center"
        >
          <div
            class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl shadow-lg border border-n-weak p-6 flex items-center gap-4"
          >
            <svg
              class="animate-spin h-8 w-8 text-n-blue-9"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                Atualizando dados...
              </p>
              <p class="text-xs text-n-slate-10 mt-1">
                Aplicando novos filtros
              </p>
            </div>
          </div>
        </div>
      </transition>

      <div class="py-6 space-y-6">
        <!-- Active Filter Banner -->
        <div
          v-if="selectedAgent"
          class="flex items-center justify-between p-4 bg-n-blue-3 border border-n-blue-6 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <span class="text-2xl">🔍</span>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                Filtrando por agente
              </p>
              <p class="text-xs text-n-slate-11">
                Mostrando apenas dados de:
                <strong>{{ selectedAgent.name }}</strong>
              </p>
            </div>
          </div>
          <button
            class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-solid-2 hover:bg-n-solid-3 rounded-lg transition-colors"
            @click="clearAgentFilter"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Limpar Filtro
          </button>
        </div>

        <!-- Metrics Cards -->
        <MetricsCards :metrics="displayMetrics" />

        <!-- Charts Row -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <DistributionChart
            :data="reportData.distribution"
            :selected-agent-id="filters.selectedAgentId"
            @agent-click="handleAgentClick"
          />

          <PerformanceChart
            :data="performanceData"
            :selected-agent-name="selectedAgent?.name"
            @agent-click="handleAgentClickByName"
          />
        </div>

        <!-- Agents Tables -->
        <div class="grid grid-cols-1 gap-6">
          <AgentsTable
            title="🟢 Agentes Ativos"
            :agents="filteredOnlineAgents"
            :selected-agent-id="filters.selectedAgentId"
            :team-id="filters.teamId"
            :last-update="lastUpdate"
            status="online"
            @agent-click="handleAgentClick"
          />

          <AgentsTable
            v-if="filteredOfflineAgents.length > 0"
            title="⚫ Agentes Offline"
            :agents="filteredOfflineAgents"
            :selected-agent-id="filters.selectedAgentId"
            :team-id="filters.teamId"
            :last-update="lastUpdate"
            status="offline"
            @agent-click="handleAgentClick"
          />
        </div>

        <!-- Timeline and Reassignments -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <TimelineList
            :timeline="filteredTimeline"
            :selected-agent-id="filters.selectedAgentId"
          />

          <ReassignmentsTable
            v-if="filteredReassignments.length > 0"
            :reassignments="filteredReassignments"
          />
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<style scoped>
/* 🔥 NOVO: Transição suave para o overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
